import { useMemo } from 'react';

import {
  Badge,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icons,
} from '@commenda/kakashi';

import { IServiceAuditorFinding } from '@commenda/api/agent-platform/service-auditor/types';

import { ServiceAuditFindingBadgeHelper } from '../helpers/ServiceAuditFindingBadgeHelper';
import { ServiceAuditFindingHelper } from '../helpers/ServiceAuditFindingHelper';
import { ServiceAuditPreviousRoundHelper } from '../helpers/ServiceAuditPreviousRoundHelper';
import { ServiceAuditReportHelper } from '../helpers/ServiceAuditReportHelper';
import { ServiceAuditFindingDetails } from './ServiceAuditFindingDetails';
import { ServiceAuditFindingFeedbackForm } from './ServiceAuditFindingFeedbackForm';

interface IServiceAuditFindingCardProps {
  finding: IServiceAuditorFinding;
  onFeedbackSaved: (() => Promise<unknown>) | null;
}

/** One finding or intake gap: badges, headline, the plain summary with the expert details folded away, any last-round hint, and feedback. */
export const ServiceAuditFindingCard = ({
  finding,
  onFeedbackSaved,
}: IServiceAuditFindingCardProps) => {
  const badges = ServiceAuditFindingBadgeHelper.getFindingBadges(finding);
  const plainSummaryItems = useMemo(
    () => ServiceAuditFindingHelper.getPlainSummaryItems(finding),
    [finding]
  );
  const previousRoundHints = useMemo(
    () => ServiceAuditPreviousRoundHelper.getHintLines(finding),
    [finding]
  );

  return (
    <Card.Root _className="gap-xl">
      {badges.length > 0 && (
        <div className="flex flex-wrap items-center gap-md">
          {badges.map((badge) => (
            <Badge key={badge.label} size="sm" variant={badge.variant}>
              {badge.label}
            </Badge>
          ))}
        </div>
      )}
      <p className="break-words txt-small-medium text-text-primary">
        {finding.headline}
      </p>
      {plainSummaryItems ? (
        <>
          <ul className="flex list-disc flex-col gap-md pl-xl">
            {plainSummaryItems.map((item) => (
              <li
                key={item.key}
                className="break-words txt-small-regular text-text-primary"
              >
                <span className="txt-small-medium">{item.label}: </span>
                {item.text}
              </li>
            ))}
          </ul>
          <Collapsible className="flex flex-col gap-xl">
            <CollapsibleTrigger asChild>
              <Button
                variant="link"
                size="xs"
                className="group self-start"
                leftIcon={
                  <Icons.ChevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
                }
              >
                {ServiceAuditReportHelper.DETAILS_AND_PROOF_LABEL}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-xl">
              <ServiceAuditFindingDetails finding={finding} />
            </CollapsibleContent>
          </Collapsible>
        </>
      ) : (
        <ServiceAuditFindingDetails finding={finding} />
      )}
      {previousRoundHints.map((hint) => (
        <p
          key={hint.key}
          className="break-words txt-extra-small-regular text-text-tertiary"
        >
          {hint.text}
        </p>
      ))}
      <ServiceAuditFindingFeedbackForm
        finding={finding}
        onFeedbackSaved={onFeedbackSaved}
      />
    </Card.Root>
  );
};
